import {
  Box,
  Link,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatNumber } from "../../../../../../infrastructure/utils/formatters";
import { ManagementWidgetShell } from "../managementDashboardWidgets";
import { buildDrillDownHref, pick } from "../managementDashboardUtils";

const HEATMAP_TABS = [
  { id: "province", label: "استان", key: "provinceHeatmap", altKey: "ProvinceHeatmap" },
  { id: "contractor", label: "پیمانکار", key: "contractorHeatmap", altKey: "ContractorHeatmap" },
  { id: "lending", label: "لندتک", key: "lendingPartnerHeatmap", altKey: "LendingPartnerHeatmap" },
];

const severityBg = {
  Critical: "#fdecea",
  Warning: "#fff8e1",
  Info: "#e8f4fd",
  3: "#fdecea",
  2: "#fff8e1",
  1: "#e8f4fd",
};

const BottleneckHeatmap = ({ loading, error, data, onRetry }) => {
  const [tab, setTab] = useState(0);
  const tabDef = HEATMAP_TABS[tab];

  const heatmap = useMemo(
    () => pick(data, tabDef.key, tabDef.altKey) || { columns: [], rows: [] },
    [data, tabDef]
  );

  const columns = heatmap.columns || heatmap.Columns || [];
  const rows = heatmap.rows || heatmap.Rows || [];

  return (
    <ManagementWidgetShell
      title="نقشه حرارتی گلوگاه‌ها"
      subheader="تأخیر به تفکیک مرحله × بعد عملیاتی"
      loading={loading}
      error={error}
      onRetry={onRetry}
    >
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 40 }}>
        {HEATMAP_TABS.map((t, idx) => (
          <Tab key={t.id} label={t.label} sx={{ minHeight: 40, fontSize: 13 }} value={idx} />
        ))}
      </Tabs>

      {!rows.length && !loading && !error ? (
        <Typography color="text.secondary">داده‌ای برای نمایش وجود ندارد.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>مرحله</TableCell>
                {columns.map((col) => (
                  <TableCell key={pick(col, "columnCode", "ColumnCode")} align="center">
                    {pick(col, "columnTitle", "ColumnTitle")}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const cells = row.cells || row.Cells || [];
                const cellMap = Object.fromEntries(
                  cells.map((c) => [pick(c, "columnCode", "ColumnCode"), c])
                );
                return (
                  <TableRow key={pick(row, "rowCode", "RowCode")}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {pick(row, "rowTitle", "RowTitle")}
                    </TableCell>
                    {columns.map((col) => {
                      const code = pick(col, "columnCode", "ColumnCode");
                      const cell = cellMap[code];
                      const count = pick(cell, "count", "Count") ?? 0;
                      const overdue = pick(cell, "overduePercent", "OverduePercent");
                      const severity = pick(cell, "severity", "Severity");
                      const href = buildDrillDownHref(pick(cell, "drillDown", "DrillDown"));
                      const bg = severityBg[severity] || "transparent";

                      const content = (
                        <Box sx={{ py: 0.5 }}>
                          <Typography fontWeight={700} fontSize={13}>
                            {formatNumber(count)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {overdue != null ? `${formatNumber(overdue, 1)}٪ SLA` : "—"}
                          </Typography>
                        </Box>
                      );

                      return (
                        <TableCell
                          key={code}
                          align="center"
                          sx={{ bgcolor: bg, minWidth: 72 }}
                        >
                          {href && count > 0 ? (
                            <Tooltip title={`میانگین: ${formatNumber(pick(cell, "averageDays", "AverageDays"), 1)} روز`}>
                              <Link component={RouterLink} to={href} underline="none" color="inherit">
                                {content}
                              </Link>
                            </Tooltip>
                          ) : (
                            content
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ManagementWidgetShell>
  );
};

export default BottleneckHeatmap;
